import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Box, Flex, Absolute, Image } from 'rebass';
import styled from 'styled-components';
import { ratio } from 'styled-system';
import { ExpansionPanel, ExpansionPanelSummary, ExpansionPanelDetails } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ReactGA from 'react-ga';

import { getSolutionDetailsAds, isBannerCTAEnabled } from 'Domain';
import { Banner } from 'Routes/MockExamDetails/Components';

import { COLOR, SPACE } from '../Theme';
import { Text, Icon, ResponsiveBox } from '../Base';
import VideoPlayer from '../VideoPlayer';
import { GATTable } from '../Table';
import LoadingView from '../Loading';

import { Header } from './Subcomponents';

const RatioBox = styled(Box)`
  ${ratio};
  height: auto;
  overflow: hidden;
  position: relative;
`;

const CORRECT_STATUS = {
  CORRECT: 1,
  INCORRECT: -1,
  NO_ANSWER: 0,
};

const getCorrectness = correct => {
  switch (correct) {
    case true:
      return CORRECT_STATUS.CORRECT;
    case false:
      return CORRECT_STATUS.INCORRECT;
    default:
      return CORRECT_STATUS.NO_ANSWER;
  }
};

const getAnswerBackgroundColor = correctStatus => {
  switch (correctStatus) {
    case CORRECT_STATUS.CORRECT:
      return COLOR.correct;
    case CORRECT_STATUS.INCORRECT:
      return COLOR.incorrect;
    default:
      return undefined;
  }
};

const getAnswerStatus = correctStatus => {
  switch (correctStatus) {
    case CORRECT_STATUS.CORRECT:
      return 'น้องตอบข้อนี้ถูก';
    case CORRECT_STATUS.INCORRECT:
      return 'น้องตอบข้อนี้ผิด';
    default:
      return undefined;
  }
};

const CandidateAnswerPanel = ({ correctStatus, right, ...restProps }) => {
  return (
    <Box bg={getAnswerBackgroundColor(correctStatus)} {...restProps}>
      <Flex justifyContent="space-between" px={SPACE.medium} py={SPACE.xsmall}>
        <Box>
          <Text>{getAnswerStatus(correctStatus)}</Text>
        </Box>
        <Box>{right && <Text>{`คำตอบที่เลือกไว้: ${right}`}</Text>}</Box>
      </Flex>
    </Box>
  );
};

const SolutionItem = ({ imageSrc }) => {
  return (
    <Box mb={SPACE.small}>
      <Image src={imageSrc} />
    </Box>
  );
};

const SolutionBox = ({ items = [], secondaryItems = [], secondaryTitle }) => {
  let secondary;
  if (secondaryItems.length) {
    secondary = (
      <ExpansionPanel>
        <ExpansionPanelSummary expandIcon={<ExpandMoreIcon />}>
          <Flex justifyContent="space-between" width={1}>
            <Text>{secondaryTitle}</Text>
            <Text>อ่านบทความ</Text>
          </Flex>
        </ExpansionPanelSummary>
        <ExpansionPanelDetails>
          {secondaryItems.map((item, i) => (
            <SolutionItem key={item + i} imageSrc={item} />
          ))}
        </ExpansionPanelDetails>
      </ExpansionPanel>
    );
  }
  return (
    <Box mb={SPACE.medium}>
      {secondary}
      {items.map((item, i) => (
        <SolutionItem key={item + i} imageSrc={item} />
      ))}
    </Box>
  );
};

const VideoTitle = styled(props => <Text.Subtitle fontWeight="bold" {...props} />)`
  text-decoration: underline;
`;

const getQuestionNumberText = (current, total) => {
  if (current && total) {
    return `ข้อ ${current}/${total}`;
  }
  return ' ';
};

const VideoWrapper = ({ children, ...restProps }) => (
  <RatioBox ratio={9 / 16} {...restProps}>
    <Absolute top={0} bottom={0} left={0} right={0}>
      {children}
    </Absolute>
  </RatioBox>
);

const handleBannerClick = event => {
  try {
    ReactGA.event({
      category: 'CAMPAIGN_MF_2562-01',
      action: 'Tab3 Solution Popup Banner Click',
    });
  } catch (err) {
    console.log('ga error:', err);
  }
  const { href, search } = event.currentTarget;
};

const renderLinkToTcasAppBanner = () => {
  const { image, link } = getSolutionDetailsAds();
  return isBannerCTAEnabled && image && link ? (
    <Banner link={link} imageSrc={image} onBannerClick={handleBannerClick} />
  ) : null;
};

class SolutionDetails extends Component {
  static propTypes = {
    title: PropTypes.string,
    correct: PropTypes.bool,
    items: PropTypes.array,
    secondaryItems: PropTypes.array,
    video: PropTypes.shape({
      src: PropTypes.string,
      type: PropTypes.string,
    }),
    candidateAnswer: PropTypes.string,
    question: PropTypes.shape({
      current: PropTypes.number,
      total: PropTypes.number,
    }),
    isGAT: PropTypes.bool,
    gatAnswers: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        title: PropTypes.string.isRequired,
        result: PropTypes.arrayOf(PropTypes.bool),
        answer: PropTypes.arrayOf(PropTypes.string),
        solution: PropTypes.arrayOf(PropTypes.string).isRequired,
        score: PropTypes.number,
        maxScore: PropTypes.number,
      }),
    ),
    loading: PropTypes.bool,
  };

  static defaultProps = {
    items: [],
    secondaryItems: [],
    question: {},
    gatAnswers: [],
  };

  render() {
    const {
      title,
      question,
      correct,
      isGAT,
      video,
      candidateAnswer,
      items,
      secondaryItems,
      secondaryTitle,
      gatAnswers,
      loading,
    } = this.props;
    const { current, total } = question;
    const banner = renderLinkToTcasAppBanner();
    return (
      <Box>
        <Box mb={SPACE.large}>
          <Header cap left={title} right={getQuestionNumberText(current, total)} />
          <CandidateAnswerPanel correctStatus={getCorrectness(correct)} right={candidateAnswer} />
          <Flex
            // flexWrap="wrap"
            flexDirection={['column-reverse', null, null, 'row']}
            px={SPACE.medium}
            py={SPACE.medium}
          >
            {loading && <LoadingView dimmer={false} loaderProps={{ active: true }} />}
            <Box flex="1 1 auto" width={[1, null, null, 7 / 10]}>
              <SolutionBox
                items={items}
                secondaryItems={secondaryItems}
                secondaryTitle={secondaryTitle}
              />
              {isGAT && (
                <Box mt={3}>
                  <GATTable value={gatAnswers} />
                </Box>
              )}
            </Box>
            {video && (
              <Box width={[1, null, null, 3 / 10]}>
                <Box p={SPACE.small} />
                <Box style={{ minWidth: '30%' }}>
                  <VideoTitle>วิดีโอเฉลย</VideoTitle>
                  <VideoWrapper>
                    <VideoPlayer src={video} autoplay={false} wrapperStyle={{ height: '100%' }} />
                  </VideoWrapper>
                  {banner && !loading && (
                    <ResponsiveBox display={['none', null, null, 'block']}>
                      <Box mt={SPACE.medium}>{banner}</Box>
                    </ResponsiveBox>
                  )}
                </Box>
              </Box>
            )}
          </Flex>
          {banner && !loading && (
            <ResponsiveBox
              px={SPACE.medium}
              display={['block', 'block', null, `${video ? 'none' : 'block'}`]}
            >
              <Box mt={SPACE.medium}>{banner}</Box>
            </ResponsiveBox>
          )}
        </Box>
      </Box>
    );
  }
}

export default SolutionDetails;
